import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Submission, Task, Participant, ParticipantGroup } from '../../../types';
import { ResearcherFeedCard } from './ResearcherFeedCard';
import './ResearcherFeed.css';

export interface ResearcherFeedProps {
  submissions: Submission[];
  tasks: Task[];
  participants: Participant[];
  participantsMap: Map<string, Participant>;
  threadContext: {
    currentUserUid: string;
    currentUserName: string;
    organizationId: string;
  };
  participantGroups: ParticipantGroup[];
  onUpdateSubmission: (id: string, updates: Partial<Submission>) => Promise<void>;
  theme?: 'dark' | 'light';
  onToggleTheme?: () => void;
}

type MediaFilter = 'all' | 'photo' | 'video' | 'audio' | 'text' | 'screen' | 'signification';
type StatusFilter = 'all' | 'unreviewed' | 'starred';
type SortMode = 'newest' | 'oldest';

/** Compute a day label from a timestamp */
function dayLabel(raw: any): { key: string; label: string } {
  if (!raw) return { key: 'unknown', label: 'Unknown' };
  const d = raw.toDate ? raw.toDate() : new Date(raw);
  if (isNaN(d.getTime())) return { key: 'unknown', label: 'Unknown' };

  const key = d.toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  if (key === today) return { key, label: 'Today' };
  if (key === yesterday) return { key, label: 'Yesterday' };
  return {
    key,
    label: d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
  };
}

export const ResearcherFeed: React.FC<ResearcherFeedProps> = ({
  submissions,
  tasks,
  participants,
  participantsMap,
  participantGroups,
  threadContext,
  onUpdateSubmission,
  theme = 'dark',
  onToggleTheme,
}) => {
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [participantFilter, setParticipantFilter] = useState<string | null>(null);
  const [participantGroupFilter, setParticipantGroupFilter] = useState<string | null>(null);
  const [taskGroupFilter, setTaskGroupFilter] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const taskMap = useMemo(() => new Map(tasks.map(t => [t.id, t])), [tasks]);

  /* ── Participants with submissions — for avatar bar ── */
  const activeParticipants = useMemo(() => {
    const withSubs = new Set(submissions.map(s => s.participantId));
    return participants.filter(p => withSubs.has(p.id));
  }, [submissions, participants]);

  /* ── Unique task groups (from task.customGroup) ── */
  const taskGroups = useMemo(() => {
    const groups = new Set<string>();
    tasks.forEach(t => { if (t.customGroup) groups.add(t.customGroup); });
    return Array.from(groups).sort();
  }, [tasks]);

  /* ── Participant group membership lookup ── */
  const participantToGroups = useMemo(() => {
    const map = new Map<string, string[]>();
    participantGroups.forEach(g => {
      g.participantIds.forEach(pid => {
        const existing = map.get(pid) || [];
        existing.push(g.id);
        map.set(pid, existing);
      });
    });
    return map;
  }, [participantGroups]);

  /* ── Filter + sort ── */
  const filtered = useMemo(() => {
    let list = [...submissions];

    if (mediaFilter !== 'all') {
      list = list.filter(s => s.mediaType === mediaFilter);
    }
    if (statusFilter === 'unreviewed') {
      list = list.filter(s => !s.isReviewed);
    } else if (statusFilter === 'starred') {
      list = list.filter(s => s.isStarred);
    }
    if (participantFilter) {
      list = list.filter(s => s.participantId === participantFilter);
    }
    if (participantGroupFilter) {
      const group = participantGroups.find(g => g.id === participantGroupFilter);
      if (group) {
        const memberSet = new Set(group.participantIds);
        list = list.filter(s => memberSet.has(s.participantId));
      }
    }
    if (taskGroupFilter) {
      const matchingTaskIds = new Set(tasks.filter(t => t.customGroup === taskGroupFilter).map(t => t.id));
      list = list.filter(s => s.taskId && matchingTaskIds.has(s.taskId));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        s.notes?.toLowerCase().includes(q) ||
        s.transcript?.toLowerCase().includes(q) ||
        s.tags?.some(t => t.toLowerCase().includes(q)) ||
        participantsMap.get(s.participantId)?.name?.toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      const ta = new Date(a.timestamp || a.createdAt || 0).getTime();
      const tb = new Date(b.timestamp || b.createdAt || 0).getTime();
      return sortMode === 'newest' ? tb - ta : ta - tb;
    });

    return list;
  }, [submissions, mediaFilter, statusFilter, participantFilter, participantGroupFilter, taskGroupFilter, sortMode, searchQuery, participantsMap, participantGroups, tasks]);

  /* ── Infinite scroll — show 20 at a time ── */
  const [visibleCount, setVisibleCount] = useState(20);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && visibleCount < filtered.length) {
          setVisibleCount(prev => Math.min(prev + 20, filtered.length));
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visibleCount, filtered.length]);

  // Reset visible count when filters change
  useEffect(() => { setVisibleCount(20); }, [mediaFilter, statusFilter, participantFilter, participantGroupFilter, taskGroupFilter, searchQuery]);

  /* ── Scroll to submission from ?sub= query param ── */
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollToSubId = searchParams.get('sub');

  useEffect(() => {
    if (!scrollToSubId || filtered.length === 0) return;
    // Ensure the target submission is in the visible slice
    const idx = filtered.findIndex(s => s.id === scrollToSubId);
    if (idx >= 0 && idx >= visibleCount) {
      setVisibleCount(idx + 5);
    }
  }, [scrollToSubId, filtered, visibleCount]);

  useEffect(() => {
    if (!scrollToSubId) return;
    const el = document.getElementById(`feed-sub-${scrollToSubId}`);
    if (!el) return;
    // Small delay for DOM to settle after visibleCount expansion
    const timer = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.outline = '2px solid var(--action, #D4715A)';
      el.style.outlineOffset = '2px';
      el.style.borderRadius = '12px';
      setTimeout(() => { el.style.outline = ''; el.style.outlineOffset = ''; }, 2500);
      // Clear the query param so refresh doesn't re-scroll
      setSearchParams({}, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [scrollToSubId, visibleCount, setSearchParams]);

  const visibleSubmissions = filtered.slice(0, visibleCount);

  /* ── Actions ── */
  const handleToggleStar = useCallback((id: string) => {
    const sub = submissions.find(s => s.id === id);
    if (sub) onUpdateSubmission(id, { isStarred: !sub.isStarred });
  }, [submissions, onUpdateSubmission]);

  const handleToggleReview = useCallback((id: string) => {
    const sub = submissions.find(s => s.id === id);
    if (sub) onUpdateSubmission(id, { isReviewed: !sub.isReviewed });
  }, [submissions, onUpdateSubmission]);

  const handleRequestRedo = useCallback((id: string) => {
    // TODO: wire to redo request flow
    console.log('Redo requested for submission:', id);
  }, []);

  const MEDIA_CHIPS: { key: MediaFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'photo', label: 'Photo' },
    { key: 'video', label: 'Video' },
    { key: 'audio', label: 'Audio' },
    { key: 'text', label: 'Text' },
    { key: 'screen', label: 'Screen' },
    { key: 'signification', label: 'Signal' },
  ];

  const STATUS_CHIPS: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'unreviewed', label: 'Unreviewed' },
    { key: 'starred', label: 'Starred' },
  ];

  return (
    <div className={`rf-page ${theme === 'light' ? 'rf-light' : ''}`}>
      {/* ── Header ── */}
      <div className="rf-header">
        <div className="rf-header-left">
          <h1 className="rf-title">Feed</h1>
          <span className="rf-subtitle">{filtered.length} submissions</span>
        </div>
        <div className="rf-header-actions">
          {onToggleTheme && (
            <button
              className="rf-theme-btn"
              onClick={onToggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
          )}
          <button
            className={`rf-sort-btn ${sortMode === 'oldest' ? 'rf-sort--asc' : ''}`}
            onClick={() => setSortMode(m => m === 'newest' ? 'oldest' : 'newest')}
            aria-label="Toggle sort order"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
          </button>
          <button
            className={`rf-search-toggle ${showSearch ? 'rf-search--active' : ''}`}
            onClick={() => setShowSearch(p => !p)}
            aria-label="Search"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Search bar (expandable) ── */}
      {showSearch && (
        <div className="rf-search-bar">
          <input
            className="rf-search-input"
            type="text"
            placeholder="Search notes, tags, participants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button className="rf-search-clear" onClick={() => setSearchQuery('')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* ── Participant avatar bar (stories-style) ── */}
      {activeParticipants.length > 0 && (
        <div className="rf-avatars">
          <button
            className={`rf-avatar-pill ${!participantFilter ? 'rf-avatar-pill--active' : ''}`}
            onClick={() => setParticipantFilter(null)}
          >
            <span className="rf-avatar-all">All</span>
          </button>
          {activeParticipants.map(p => (
            <button
              key={p.id}
              className={`rf-avatar-pill ${participantFilter === p.id ? 'rf-avatar-pill--active' : ''}`}
              onClick={() => setParticipantFilter(prev => prev === p.id ? null : p.id)}
            >
              <div className="rf-avatar-ring">
                {p.avatarUrl ? (
                  <img src={p.avatarUrl} alt="" className="rf-avatar-thumb" />
                ) : (
                  <span className="rf-avatar-letter">{p.name.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <span className="rf-avatar-name">{p.name.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Filter chips — row 1: media + status ── */}
      <div className="rf-filters">
        <div className="rf-chip-group">
          {MEDIA_CHIPS.map(c => (
            <button
              key={c.key}
              className={`rf-chip ${mediaFilter === c.key ? 'rf-chip--active' : ''}`}
              onClick={() => setMediaFilter(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="rf-chip-group">
          {STATUS_CHIPS.map(c => (
            <button
              key={c.key}
              className={`rf-chip ${statusFilter === c.key ? 'rf-chip--active' : ''}`}
              onClick={() => setStatusFilter(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Filter chips — row 2: participant groups + task groups ── */}
      {(participantGroups.length > 0 || taskGroups.length > 0) && (
        <div className="rf-filters">
          {participantGroups.length > 0 && (
            <div className="rf-chip-group">
              <span className="rf-chip-label">Group</span>
              <button
                className={`rf-chip ${!participantGroupFilter ? 'rf-chip--active' : ''}`}
                onClick={() => setParticipantGroupFilter(null)}
              >
                All
              </button>
              {participantGroups.map(g => (
                <button
                  key={g.id}
                  className={`rf-chip ${participantGroupFilter === g.id ? 'rf-chip--active' : ''}`}
                  onClick={() => setParticipantGroupFilter(prev => prev === g.id ? null : g.id)}
                >
                  {g.name}
                </button>
              ))}
            </div>
          )}
          {taskGroups.length > 0 && (
            <div className="rf-chip-group">
              <span className="rf-chip-label">Task</span>
              <button
                className={`rf-chip ${!taskGroupFilter ? 'rf-chip--active' : ''}`}
                onClick={() => setTaskGroupFilter(null)}
              >
                All
              </button>
              {taskGroups.map(g => (
                <button
                  key={g}
                  className={`rf-chip ${taskGroupFilter === g ? 'rf-chip--active' : ''}`}
                  onClick={() => setTaskGroupFilter(prev => prev === g ? null : g)}
                >
                  {g}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Feed ── */}
      <div className="rf-scroll">
        {visibleSubmissions.length === 0 ? (
          <div className="rf-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--faint, #A09890)" strokeWidth="1.5" strokeLinecap="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21,15 16,10 5,21" />
            </svg>
            <p className="rf-empty-text">No submissions match your filters</p>
          </div>
        ) : (
          <div className="rf-feed">
            {visibleSubmissions.map((sub, i) => {
              /* ── Daily digest header ── */
              const subTs = sub.createdAt || sub.timestamp;
              const prevTs = i > 0 ? (visibleSubmissions[i - 1].createdAt || visibleSubmissions[i - 1].timestamp) : null;
              const currentDay = dayLabel(subTs);
              const prevDay = prevTs ? dayLabel(prevTs) : null;
              const showDayHeader = !prevDay || currentDay.key !== prevDay.key;

              return (
                <React.Fragment key={sub.id}>
                  {showDayHeader && (
                    <div className="rf-day-header">
                      <span className="rf-day-line" />
                      <span className="rf-day-label">{currentDay.label}</span>
                      <span className="rf-day-line" />
                    </div>
                  )}
                  <ResearcherFeedCard
                    submission={sub}
                    participant={participantsMap.get(sub.participantId)}
                    task={taskMap.get(sub.taskId || '')}
                    threadContext={threadContext}
                    onToggleStar={handleToggleStar}
                    onToggleReview={handleToggleReview}
                    onRequestRedo={handleRequestRedo}
                  />
                </React.Fragment>
              );
            })}
            {/* Infinite scroll sentinel */}
            {visibleCount < filtered.length && (
              <div ref={sentinelRef} className="rf-sentinel" />
            )}
          </div>
        )}
      </div>

    </div>
  );
};
